import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {ITracker} from "../../../models/ITracker";
import {
    fetchAddTracker,
    fetchAddTrackerPayment,
    fetchDeleteTracker,
    fetchDeleteTrackerPayment,
    fetchGetAllTracker,
    fetchGetTrackerById,
    fetchUpdateTracker
} from "./actions";

interface ITrackerState {
    list: ITracker[];
    isLoading: boolean;
    current: ITracker | null;
}

const handlePending = (state: ITrackerState) => {
    state.isLoading = true;
};

const handleRejected = (state: ITrackerState) => {
    state.isLoading = false;
};

const initialState: ITrackerState = {
    list: [],
    isLoading: false,
    current: null,
};

export const TrackerSlice = createSlice({
    name: "tracker_slice",
    initialState,
    reducers: {
        setTrackers: (state, action: PayloadAction<ITracker[]>) => {
            state.list = action.payload;
        },
        setCurrentTracker: (state, action: PayloadAction<ITracker | null>) => {
            state.current = action.payload;
        },
        addTracker: (state, action: PayloadAction<ITracker>) => {
            state.list = [...state.list, action.payload];
        },
        removeTracker: (state, action: PayloadAction<ITracker>) => {
            state.list = [...state.list.filter(tracker => tracker.id !== action.payload.id)];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAddTracker.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.list = [...state.list, action.payload];
                state.isLoading = false;
            })
            .addCase(fetchGetAllTracker.fulfilled, (state, action: PayloadAction<ITracker[]>) => {
                state.list = action.payload.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                state.isLoading = false;
            })
            .addCase(fetchGetTrackerById.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchUpdateTracker.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.list = [...state.list.map((tracker) => (tracker.id === action.payload.id ? action.payload : tracker))];
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchDeleteTracker.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.list = [...state.list.filter((tracker) => tracker.id !== action.payload.id)];
                state.isLoading = false;
            })
            .addCase(fetchAddTrackerPayment.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.list = [...state.list.map((tracker) => (tracker.id === action.payload.id ? action.payload : tracker))];
                if (state.current?.id === action.payload.id) state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchDeleteTrackerPayment.fulfilled, (state, action: PayloadAction<ITracker>) => {
                state.list = [...state.list.map((tracker) => (tracker.id === action.payload.id ? action.payload : tracker))];
                if (state.current?.id === action.payload.id) state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchAddTrackerPayment.pending, handlePending)
            .addCase(fetchAddTrackerPayment.rejected, handleRejected)
            .addCase(fetchDeleteTrackerPayment.pending, handlePending)
            .addCase(fetchDeleteTrackerPayment.rejected, handleRejected)
            .addCase(fetchAddTracker.pending, handlePending)
            .addCase(fetchAddTracker.rejected, handleRejected)
            .addCase(fetchUpdateTracker.pending, handlePending)
            .addCase(fetchUpdateTracker.rejected, handleRejected)
            .addCase(fetchGetTrackerById.pending, handlePending)
            .addCase(fetchGetTrackerById.rejected, handleRejected)
            .addCase(fetchDeleteTracker.pending, handlePending)
            .addCase(fetchDeleteTracker.rejected, handleRejected);
    },
});

export const {setTrackers, setCurrentTracker, addTracker, removeTracker} = TrackerSlice.actions;
export default TrackerSlice.reducer;
